import { ReactElement, ReactNode, useMemo } from 'react';

import { Navigate, RouteObject, useLocation } from 'react-router';

import { SELLER_ROUTES } from './seller.routes';
import { useUserGroups } from '../hooks/useUserGroups';
import { PermissionDenied } from '../Pages/FailurePages/PermissionDenied/PermissionDenied';

type NewRoutesType = {
  readonly path?: string;
  readonly index?: boolean;
  readonly element?: ReactElement;
  readonly lazy?: () => Promise<{ element: ReactElement }>;
  readonly children?: ReadonlyArray<NewRoutesType>;
  readonly accessibleByGroups?: ReadonlyArray<string>;
};

export type NewRoutesWrapperType = {
  readonly wrapper: React.ReactNode;
  readonly paths: ReadonlyArray<NewRoutesType>;
};

export type NewRoutesWrapperArrayType = ReadonlyArray<NewRoutesWrapperType>;

type TWildcard = `:${string}` | '*';
type ReplaceWildcard<S extends string> =
  S extends `${infer Head}${TWildcard}/${infer Tail}`
    ? `${Head}${string}/${ReplaceWildcard<Tail>}`
    : S;

export type ValidRoutePaths =
  (typeof SELLER_ROUTES)[number]['paths'][number]['path'] extends infer R extends
    string
    ? `/pay/${ReplaceWildcard<R>}`
    : never;

const GroupPermissionEnforcement = ({
  children,
  accessibleByGroups,
}: {
  children: ReactNode;
  accessibleByGroups?: ReadonlyArray<string>;
}) => {
  const { groups, isLoading, isStaff } = useUserGroups();
  if (isLoading) return;

  if (
    accessibleByGroups &&
    groups &&
    !accessibleByGroups.some((group) => groups.includes(group)) &&
    !isStaff
  )
    return <PermissionDenied />;

  return <>{children}</>;
};

const toChildRoutes = (element: NewRoutesType): RouteObject[] => {
  if (element.children) {
    return [
      {
        // Pathless layout: element.element renders here and its <Outlet/> hosts
        // the nested children. This lets a layout/gate element sit between the
        // outer wrapper and the nested (tab) routes.
        element: element.element,
        children: element.children.map((child) => ({
          path: child.path,
          index: child.index,
          element: child.element,
          lazy: child.lazy,
        })),
      },
    ];
  }
  return [
    {
      path: '',
      element: element.element,
      lazy: element.lazy,
    },
  ];
};

export const normaliseContainerRoutes = (
  unNormalisedWrapperRouteArray: NewRoutesWrapperArrayType,
): RouteObject[] => {
  return unNormalisedWrapperRouteArray
    .map((route) => {
      const wrapper: NewRoutesWrapperType['wrapper'] = route.wrapper;
      const routes: NewRoutesWrapperType['paths'] = route.paths;
      return routes.map((element) => ({
        path: element.path,
        element: !element.accessibleByGroups ? (
          wrapper
        ) : (
          <GroupPermissionEnforcement
            accessibleByGroups={element.accessibleByGroups}
          >
            {wrapper}
          </GroupPermissionEnforcement>
        ),
        children: toChildRoutes(element),
      })) as RouteObject;
    })
    .flat(1);
};

export const SafeNavigate = ({
  to,
  replace = false,
}: {
  to: string;
  replace?: boolean;
}) => {
  const location = useLocation();
  const url = useMemo(
    () => (location.search ? `${to.replace(/\/$/, '')}${location.search}` : to),
    [location.search, to],
  );

  return <Navigate to={url} replace={replace} />;
};
